import {
  DashboardState,
  DesignTimeErrorBoundaryData,
  EScreenType,
  ESdChangeReason,
  FrameMessage,
  FrameMessageType,
  IAddChildItemMessage,
  IChangeDashboardStateMessage,
  IChangePositionMessage,
  ICloneItemMessage,
  ICopyItemMessage,
  IDetachFormMessage,
  IDisplayItemComponent,
  IDragDropByCreateItemMessage,
  IDragDropByMoveItemMessage,
  IExceptionHandlingMessage,
  IInjectDisplayItemToTargetDisplayItemMessage,
  IMergeStaticTableColumnsMessage,
  ICursorMovedMessage,
  IPasteItemMessage,
  IPasteItemStyleMessage,
  IPropertyChangedMessage,
  IRemoveItemMessage,
  ISelectItemMessage,
  IShowFormPopupMessage,
  IShowWorkflowBusinessObjectPopupMessage,
  ISelectionGeometryMessage,
  LeftPanelType,
  RightPanelType,
  SpecialDIIds
} from "@kuika/kuika-cl-model";
import { ISaveBlockAsCustomBlockMessage } from "@kuika/kuika-cl-model/src/messages/frame-messages/save-block-as-custom-block-message";
import { IShowActionPopupMessage } from "@kuika/kuika-cl-model/src/messages/frame-messages/show-action-popup-message";
import { selectionGeometryStore } from "../components/design-tools/canvas/selection-overlay/selection-geometry-store";
import { ScreenDesignContextProvider } from "../contexts/screen-design-context/screen-design-context-provider";
import { DragDrop } from "../shared/utility/drag-drop-helper";
import { KMainFunctions } from "../shared/utility/main-functions";
import { ScreenDesignHelper } from "../shared/utility/screen-design-helper";
import { StaticTableHelper } from "../shared/utility/static-table-helper";

declare let window: any;

interface IFrameMessageEnvelope {
  messageType: FrameMessageType;
  message: FrameMessage;
  messageId: string;
}

export class FrameMessageReceiver {
  sdContext: ScreenDesignContextProvider;

  dragDrop: DragDrop;

  // Dedup state for the 200ms duplicate-message window. Instance fields (not
  // window globals) so the last message payload isn't pinned on window and is
  // released with the receiver.
  private lastEvent: IFrameMessageEnvelope | undefined;

  private lastEventDate: number = 0;

  constructor(sdContext: ScreenDesignContextProvider) {
    this.sdContext = sdContext;
    this.dragDrop = new DragDrop(sdContext);
  }

  receive = (iframeMessage: { messageType: FrameMessageType; message: FrameMessage; messageId: string }) => {
    // iframeMessage.message = Decompress(iframeMessage.message as any);
    // console.log("<<<<<< Designer Received a Message From (" + FrameMessageType[iframeMessage.messageType].toString()+", Message ID:"  + iframeMessage.messageId + ", " + new Date().getSeconds() + "-" + new Date().getMilliseconds() + " )");
    // console.log(iframeMessage.message);
    // console.log("<<<<<<");
    if (!iframeMessage || !iframeMessage.message || !iframeMessage.messageType) {
      return;
    }
    // Selection geometry streams on every scroll/resize tick. It goes straight to its own store — never
    // through sd state (no withSD re-render per tick) and never through the 200ms dedup below, where it
    // would become `lastEvent` and let a duplicate clone/copy message that straddles it slip through.
    if (iframeMessage.messageType === FrameMessageType.selectionGeometryMessage) {
      selectionGeometryStore.publish(iframeMessage.message as ISelectionGeometryMessage);
      return;
    }
    if (this.lastEventDate && new Date().getTime() - this.lastEventDate <= 200) {
      if (this.lastEvent && JSON.stringify(this.lastEvent) === JSON.stringify(iframeMessage)) {
        return;
      }
    }
    this.lastEvent = iframeMessage;
    this.lastEventDate = new Date().getTime();

    this.dispatchMessage(iframeMessage);
  };

  private dispatchMessage = (iframeMessage: {
    messageType: FrameMessageType;
    message: FrameMessage;
    messageId: string;
  }) => {
    switch (iframeMessage.messageType) {
      case FrameMessageType.selectItemMessage: {
        const message: ISelectItemMessage = iframeMessage.message as ISelectItemMessage;
        this.selectItemMessage(message);
        break;
      }
      case FrameMessageType.cursorMovedMessage: {
        const message: ICursorMovedMessage = iframeMessage.message as ICursorMovedMessage;
        this.sdContext.collabProvider.onLocalCursorMoved(message.x, message.y);
        break;
      }
      case FrameMessageType.cloneItemMessage: {
        const message: ICloneItemMessage = iframeMessage.message as ICloneItemMessage;
        this.cloneItemMessage(message);
        break;
      }
      case FrameMessageType.dragDropByCreateItemMessage: {
        const message: IDragDropByCreateItemMessage = iframeMessage.message as IDragDropByCreateItemMessage;
        this.dragDropByCreateItemMessage(message);
        break;
      }
      case FrameMessageType.dragDropByMoveItemMessage: {
        const message: IDragDropByMoveItemMessage = iframeMessage.message as IDragDropByMoveItemMessage;
        this.dragDropByMoveItemMessage(message);
        break;
      }
      case FrameMessageType.addChildItemMessage: {
        const message: IAddChildItemMessage = iframeMessage.message as IAddChildItemMessage;
        this.addChildItemMessage(message);
        break;
      }
      case FrameMessageType.exceptionHandlingMessage: {
        const message: IExceptionHandlingMessage = iframeMessage.message as IExceptionHandlingMessage;
        this.exceptionHandlingMessage(message);
        break;
      }
      case FrameMessageType.removeItemMessage: {
        const message: IRemoveItemMessage = iframeMessage.message as IRemoveItemMessage;
        this.removeItemMessage(message);
        break;
      }
      case FrameMessageType.changePositionMessage: {
        const message: IChangePositionMessage = iframeMessage.message as IChangePositionMessage;
        this.changePositionMessage(message);
        break;
      }
      case FrameMessageType.propertyChangedMessage: {
        const message: IPropertyChangedMessage = iframeMessage.message as IPropertyChangedMessage;
        this.propertyChangedMessage(message);
        break;
      }
      case FrameMessageType.copyItemMessage: {
        const message: ICopyItemMessage = iframeMessage.message as ICopyItemMessage;
        this.copyItemMessage(message);
        break;
      }
      case FrameMessageType.pasteItemMessage: {
        const message: IPasteItemMessage = iframeMessage.message as IPasteItemMessage;
        this.pasteItemMessage(message);
        break;
      }
      case FrameMessageType.pasteItemStyleMessage: {
        const message: IPasteItemStyleMessage = iframeMessage.message as IPasteItemStyleMessage;
        this.pasteItemStyleMessage(message);
        break;
      }
      case FrameMessageType.getClipboardValue: {
        this.getClipboardValue();
        break;
      }
      case FrameMessageType.undoMessage: {
        this.undoMessage();
        break;
      }
      case FrameMessageType.redoMessage: {
        this.redoMessage();
        break;
      }
      case FrameMessageType.saveScreenMessage: {
        this.saveScreenMessage();
        break;
      }
      case FrameMessageType.changeDashboardStateMessage: {
        const message: IChangeDashboardStateMessage = iframeMessage.message as IChangeDashboardStateMessage;
        this.changeDashboardStateMessage(message);
        break;
      }
      case FrameMessageType.setNewScreenModalVisibilityMessage: {
        this.setNewScreenModalVisibilityMessage();
        break;
      }
      case FrameMessageType.setActiveLeftPanelByClickMessage: {
        const message: any = iframeMessage.message as any;
        this.setActiveLeftPanelByClickMessage(message);
        break;
      }

      case FrameMessageType.setActiveRightPanelByClickMessage: {
        const message: any = iframeMessage.message as any;
        this.setActiveRightPanelByClickMessage(message);
        break;
      }

      case FrameMessageType.showActionPopup: {
        const message: IShowActionPopupMessage = iframeMessage.message as IShowActionPopupMessage;

        if (
          ScreenDesignHelper.getIsChildOfDataRepeater(
            parseInt(message.targetKuikaId, 10),
            this.sdContext.screenProvider.getScreenDesignDictionary()
          )
        ) {
          KMainFunctions.displayWarningNotification("You can not drag action into a data repeater");
          return;
        }

        if (message.actionDefinition.name === "StartProcess") {
          this.sdContext.wizardProvider.setIsPickProcessModalOpen(true);
          this.sdContext.wizardProvider.setTargetKuikaIdForCreateElementForAction(message.targetKuikaId);
          this.sdContext.wizardProvider.setActionDefinitionForCreateElementForAction(message.actionDefinition);
          break;
        }

        this.sdContext.wizardProvider.setIsElementPickerWithActionOpen(true);
        this.sdContext.wizardProvider.setActionDefinitionForCreateElementForAction(message.actionDefinition);
        this.sdContext.wizardProvider.setTargetKuikaIdForCreateElementForAction(message.targetKuikaId);
        break;
      }

      case FrameMessageType.showWorkflowBusinessObjectPopup: {
        const message = iframeMessage.message as IShowWorkflowBusinessObjectPopupMessage;
        this.sdContext.wizardProvider.setProcessIdForWizardForBusinessObject(message.processId);
        this.sdContext.wizardProvider.setTargetKuikaIdForFormWizard(message.targetKuikaId);
        this.sdContext.wizardProvider.setIsWizardForFormOpen(true);
        break;
      }

      case FrameMessageType.showFormPopup: {
        const message = iframeMessage.message as IShowFormPopupMessage;
        this.sdContext.wizardProvider.setIsWizardForFormOpen(true);
        this.sdContext.wizardProvider.setTargetKuikaIdForFormWizard(message.targetKuikaId);
        break;
      }

      case FrameMessageType.injectDisplayItemToTargetDisplayItemMessage: {
        const message: IInjectDisplayItemToTargetDisplayItemMessage =
          iframeMessage.message as IInjectDisplayItemToTargetDisplayItemMessage;
        this.injectDisplayItemToTargetDisplayItemMessage(message);
        break;
      }

      case FrameMessageType.replaceDisplayItemToTargetDisplayItemMessage: {
        const message: IInjectDisplayItemToTargetDisplayItemMessage =
          iframeMessage.message as IInjectDisplayItemToTargetDisplayItemMessage;
        this.replaceDisplayItemToTargetDisplayItemMessage(message);
        break;
      }

      case FrameMessageType.detachFormMessage: {
        const message: IDetachFormMessage = iframeMessage.message as IDetachFormMessage;
        this.sdContext.displayItemProvider.detachForm(message.targetKuikaId);
        break;
      }

      case FrameMessageType.mergeStaticTableColumnsMessage: {
        const message: IMergeStaticTableColumnsMessage = iframeMessage.message as IMergeStaticTableColumnsMessage;
        this.mergeStaticTableColumnsMessage(message);
        break;
      }

      case FrameMessageType.saveBlockAsCustomBlockMessage: {
        const message: ISaveBlockAsCustomBlockMessage = iframeMessage.message as ISaveBlockAsCustomBlockMessage;
        this.saveBlockAsCustomBlockMessage(message);
        break;
      }

      default: {
        break;
      }
    }
  };

  propertyChangedMessage(message: IPropertyChangedMessage) {
    if (message.shouldClosePropertyExpand) {
      this.sdContext.propertyProvider.removeItemFromExpandedPropertyList(message.propertyName);
    }

    this.sdContext.propertyProvider.applyPropertyGridChangesToDisplay(
      { kuikaId: message.kuikaId, propertyName: message.propertyName, value: message.value },
      ESdChangeReason.ChangePropertyValue
    );
  }

  changePositionMessage(message: IChangePositionMessage) {
    this.dragDrop.changePosition(message.kuikaId, message.fixedPosition, message.absolutePositionInfo);
  }

  selectItemMessage = (message: ISelectItemMessage) => {
    // When the iframe sends kuikaId "0" (clearSelectedDi), it may be a stale reaction
    // from drawDesignWrapper/highlightSelectedItem triggered by renderFrame after a
    // tool-driven selection (e.g. ElementBandsBar click). Ignore it so the tool selection sticks.
    if (
      (message.kuikaid === "0" || message.kuikaid === SpecialDIIds.rootDI.toString()) &&
      this.sdContext.screenProvider.state?.changeReason === ESdChangeReason.ChangeSelectedDiByTools
    ) {
      return;
    }

    if (this.sdContext.designSituationProvider.getActiveLeftPanel() === LeftPanelType.Screens) {
      this.sdContext.designSituationProvider.setActiveLeftPanelByClick(LeftPanelType.Component);
    }

    this.sdContext.screenProvider.setScreenData(ESdChangeReason.ChangeSelectedDiByCanvasFrame, message.kuikaid);
  };

  cloneItemMessage = (message: ICloneItemMessage) => {
    this.dragDrop.cloneDI(message.kuikaid);
  };

  dragDropByCreateItemMessage = (message: IDragDropByCreateItemMessage) => {
    this.dragDrop.dragDropByCreateItemMessage(message);
  };

  dragDropByMoveItemMessage = (message: IDragDropByMoveItemMessage) => {
    this.dragDrop.dragDropByMoveItemMessage(message);
  };

  injectDisplayItemToTargetDisplayItemMessage = (message: IInjectDisplayItemToTargetDisplayItemMessage) => {
    this.dragDrop.injectDisplayItemToTargetDisplayItem(message);
  };

  replaceDisplayItemToTargetDisplayItemMessage = (message: IInjectDisplayItemToTargetDisplayItemMessage) => {
    this.dragDrop.replaceDisplayItemToTargetDisplayItem(message);
  };

  addChildItemMessage = (message: IAddChildItemMessage) => {
    const sd = this.dragDrop.screenContext.screenProvider.getScreenDesign();
    const dict = this.dragDrop.screenContext.screenProvider.getScreenDesignDictionary();
    const targetDI = dict[parseInt(message.kuikaid, 10)];
    if (!targetDI) {
      return;
    }
    const newComponent: IDisplayItemComponent | undefined = ScreenDesignHelper.createNewComponentById(
      message.childComponentId,
      this.dragDrop.screenContext
    );
    if (newComponent) {
      const project = this.dragDrop.screenContext.projectProvider.getProject();
      const newDI = ScreenDesignHelper.createNewDisplayItemByComponent(
        sd,
        this.dragDrop.screenContext.screenProvider.getScreenDesignDictionary(),
        targetDI.id,
        newComponent,
        project.componentSetObj.components
      );
      this.dragDrop.placeToLastChildItem(sd, targetDI, newDI);
    }
  };

  exceptionHandlingMessage = (message: IExceptionHandlingMessage) => {
    const obj = {
      error: message.error,
      errorInfo: message.errorInfo
    } as DesignTimeErrorBoundaryData;

    this.sdContext.exceptionHandleProvider.exceptionHandling(obj);
  };

  removeItemMessage = (message: IRemoveItemMessage) => {
    this.dragDrop.removeItem(message.kuikaid);
  };

  copyItemMessage = (message: ICopyItemMessage) => {
    this.dragDrop.copyItem(message.kuikaid);
  };

  pasteItemMessage = (message: IPasteItemMessage) => {
    this.dragDrop.pasteItem(message.kuikaid);
  };

  pasteItemStyleMessage = (message: IPasteItemStyleMessage) => {
    this.dragDrop.pasteItemStyle(message.kuikaid);
  };

  getClipboardValue = () => {
    this.dragDrop.getClipboardValue();
  };

  undoMessage = () => {
    this.sdContext.screenProvider.undo();
  };

  redoMessage = () => {
    this.sdContext.screenProvider.redo();
  };

  saveScreenMessage = () => {
    this.sdContext.screenProvider.saveScreen();
  };

  setNewScreenModalVisibilityMessage = () => {
    this.sdContext.projectProvider.setNewScreenModalVisibility(true);
  };

  changeDashboardStateMessage = (message: IChangeDashboardStateMessage) => {
    switch (message.dashboardState) {
      case DashboardState.design:
        this.redirectToUIDesign();
        break;

      case DashboardState.dataDiagramScheme:
        this.redirectToDatasources();
        break;

      case DashboardState.workFlow:
        this.redirectToWorkflow();
        break;

      case DashboardState.reportDesigner:
        this.redirectToReportDesigner();
        break;

      case DashboardState.notificationTemplate:
        this.redirectToNotificationTemplate();
        break;

      case DashboardState.apiManagement:
        this.redirectToApiBuilder();
        break;

      case DashboardState.configManager:
        this.redirectToConfigurationManager();
        break;

      case DashboardState.runSettings:
        this.redirectToRunSettings();
        break;

      case DashboardState.zendesk:
        this.redirectToHelpDesk();
        break;

      case DashboardState.scheduledJobs:
        this.redirectToScheduledJobs();
        break;

      case DashboardState.branching:
        this.redirectToBranching();
        break;

      default:
        break;
    }
  };

  setActiveLeftPanelByClickMessage = (message: any) => {
    this.sdContext.designSituationProvider.setActiveLeftPanelByClick(message);
  };

  setActiveRightPanelByClickMessage = (message: any) => {
    this.sdContext.designSituationProvider.setActiveRightPanelByClick(message);
  };

  async reloadScreenList() {
    try {
      this.sdContext.screenProvider.setScreenList();
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    }
  }

  loadScreen = async () => {
    const { reportDesignProvider, screenProvider, projectProvider, designSituationProvider } = this.sdContext;
    const isSelectedScreenIsReportDesign = reportDesignProvider.isReportDesignPageSelected();
    const isComingFromReportDesigner = designSituationProvider.state.dashboardState === DashboardState.reportDesigner;

    if (isSelectedScreenIsReportDesign || isComingFromReportDesigner) {
      if (isSelectedScreenIsReportDesign) {
        await screenProvider.saveScreen(true);
      }
      await this.reloadScreenList();

      const { screenList } = screenProvider.state;
      const { lastVisitedScreenId } = projectProvider.state.project;

      if (lastVisitedScreenId) {
        screenProvider.loadScreen(lastVisitedScreenId, true);
      } else {
        const screenId: any = screenList.find(
          (x) =>
            x.publicMetaData.screenType !== EScreenType.Report &&
            x.publicMetaData.screenType !== EScreenType.Master &&
            x.publicMetaData.screenType !== EScreenType.Component
        )?.id;
        if (screenId) {
          screenProvider.loadScreen(screenId, true);
        }
      }
    }
  };

  redirectToUIDesign = () => {
    if (this.sdContext.designSituationProvider.state.dashboardState === DashboardState.design) return;
    this.sdContext.designSituationProvider.setDashboardState(DashboardState.design);
    this.sdContext.designSituationProvider.setActiveRightPanelByClick(RightPanelType.Properties);

    this.sdContext.notificationTemplateProvider.setCreateNotificationTemplateActive();
    this.sdContext.notificationTemplateProvider.clearNotificationTemplatePanel();
    this.sdContext.scheduledJobsProvider.cancelEditingScheduledJob();
    this.sdContext.apiManagementProvider.cancelEditingApi();
    this.sdContext.apiManagementProvider.cancelActiveMethod();
    this.sdContext.datasourceProvider.cancelCreatingOrEditingDatasource();
    this.sdContext.configManagerProvider.cancelCreatingOrEditingConfiguration();
    this.sdContext.workflowProvider.clearSelectedWf();
    this.loadScreen();
  };

  redirectToDatasources = () => {
    if (this.sdContext.designSituationProvider.state.dashboardState === DashboardState.dataDiagramScheme) return;

    this.sdContext.designSituationProvider.setDashboardState(DashboardState.dataDiagramScheme);
    this.sdContext.designSituationProvider.updateDatabaseDiagramData();

    this.sdContext.notificationTemplateProvider.setCreateNotificationTemplateActive();
    this.sdContext.notificationTemplateProvider.clearNotificationTemplatePanel();
    this.sdContext.scheduledJobsProvider.cancelEditingScheduledJob();
    this.sdContext.apiManagementProvider.cancelEditingApi();
    this.sdContext.apiManagementProvider.cancelActiveMethod();
    this.sdContext.datasourceProvider.cancelCreatingOrEditingDatasource();
    this.sdContext.configManagerProvider.cancelCreatingOrEditingConfiguration();
  };

  redirectToWorkflow = async () => {
    if (this.sdContext.designSituationProvider.state.dashboardState === DashboardState.workFlow) return;

    // VTO: This constraint is removed via WFEngine architecture
    // try {
    //   const res = await WorkFlowService.CheckWorkflowInMultipleProjects(
    //     this.sdContext.projectProvider.state.project.id
    //   );
    //   if (res === false) {
    //     return;
    //   }
    // } catch (err) {
    //   return;
    // }
    if (KMainFunctions.isDomainHasAccess("Workflow") === false) {
      // this.props.showDomainAccessRequiredModal("Workflow");
      return;
    }

    this.sdContext.displayItemProvider.setSelectedDisplayItem(SpecialDIIds.rootDI.toString());
    this.sdContext.designSituationProvider.setActiveWorkFlowLeftPanel(LeftPanelType.WorkFlowList);
    if (!this.sdContext.workflowProvider.state.lastOpenWfId) this.sdContext.workflowProvider.setCreatingWfActive(true);
    this.sdContext.notificationTemplateProvider.setCreateNotificationTemplateActive();
    this.sdContext.notificationTemplateProvider.clearNotificationTemplatePanel();
    this.sdContext.scheduledJobsProvider.cancelEditingScheduledJob();
    this.sdContext.apiManagementProvider.cancelEditingApi();
    this.sdContext.apiManagementProvider.cancelActiveMethod();
    this.sdContext.datasourceProvider.cancelCreatingOrEditingDatasource();
    this.sdContext.configManagerProvider.cancelCreatingOrEditingConfiguration();
    if (this.sdContext.workflowProvider.state.lastOpenWfId) {
      this.sdContext.workflowProvider.setSelectedWf(this.sdContext.workflowProvider.state.lastOpenWfId);
    }
    this.sdContext.designSituationProvider.setActiveWorkFlowRightPanel(RightPanelType.Properties);
    // this.sdContext.state.getTicketNotifyCount();
  };

  redirectToNotificationTemplate = () => {
    if (this.sdContext.designSituationProvider.state.dashboardState === DashboardState.notificationTemplate) return;

    this.sdContext.designSituationProvider.setDashboardState(DashboardState.notificationTemplate);

    this.sdContext.notificationTemplateProvider.setCreateNotificationTemplateActive();
    this.sdContext.scheduledJobsProvider.cancelEditingScheduledJob();
    this.sdContext.apiManagementProvider.cancelEditingApi();
    this.sdContext.apiManagementProvider.cancelActiveMethod();
    this.sdContext.datasourceProvider.cancelCreatingOrEditingDatasource();
    this.sdContext.configManagerProvider.cancelCreatingOrEditingConfiguration();
    this.sdContext.workflowProvider.clearSelectedWf();
  };

  redirectToReportDesigner = () => {
    if (this.sdContext.designSituationProvider.state.dashboardState === DashboardState.reportDesigner) return;

    this.sdContext.designSituationProvider.enterReportDesigner({ showLanding: true });

    this.sdContext.notificationTemplateProvider.setCreateNotificationTemplateActive();
    this.sdContext.notificationTemplateProvider.clearNotificationTemplatePanel();
    this.sdContext.scheduledJobsProvider.cancelEditingScheduledJob();
    this.sdContext.apiManagementProvider.cancelEditingApi();
    this.sdContext.apiManagementProvider.cancelActiveMethod();
    this.sdContext.datasourceProvider.cancelCreatingOrEditingDatasource();
    this.sdContext.configManagerProvider.cancelCreatingOrEditingConfiguration();
    this.sdContext.workflowProvider.clearSelectedWf();
  };

  redirectToScheduledJobs = () => {
    if (this.sdContext.designSituationProvider.state.dashboardState === DashboardState.scheduledJobs) return;

    if (KMainFunctions.isDomainHasAccess("Scheduled Jobs") === false) {
      // this.props.showDomainAccessRequiredModal("Scheduled Jobs");
      return;
    }
    this.sdContext.designSituationProvider.setDashboardState(DashboardState.scheduledJobs);
    this.sdContext.notificationTemplateProvider.setCreateNotificationTemplateActive();
    this.sdContext.notificationTemplateProvider.clearNotificationTemplatePanel();
    this.sdContext.scheduledJobsProvider.cancelEditingScheduledJob();
    this.sdContext.apiManagementProvider.cancelEditingApi();
    this.sdContext.apiManagementProvider.cancelActiveMethod();
    this.sdContext.datasourceProvider.cancelCreatingOrEditingDatasource();
    this.sdContext.configManagerProvider.cancelCreatingOrEditingConfiguration();
    this.sdContext.workflowProvider.clearSelectedWf();
    this.sdContext.scheduledJobsProvider.setCreateSJobActive(true);
    this.sdContext.designSituationProvider.setActiveScheduledJobsPanel(LeftPanelType.None);
    // this.sdContext.state.getTicketNotifyCount();
  };

  redirectToApiBuilder = () => {
    if (this.sdContext.designSituationProvider.state.dashboardState === DashboardState.apiManagement) return;

    this.sdContext.designSituationProvider.setDashboardState(DashboardState.apiManagement);

    this.sdContext.notificationTemplateProvider.setCreateNotificationTemplateActive();
    this.sdContext.notificationTemplateProvider.clearNotificationTemplatePanel();
    this.sdContext.scheduledJobsProvider.cancelEditingScheduledJob();
    this.sdContext.datasourceProvider.cancelCreatingOrEditingDatasource();
    this.sdContext.configManagerProvider.cancelCreatingOrEditingConfiguration();
    this.sdContext.workflowProvider.clearSelectedWf();
    this.sdContext.designSituationProvider.setActiveApiManagementPanel(LeftPanelType.ApiManagement);
    // this.sdContext.state.getTicketNotifyCount();
  };

  redirectToConfigurationManager = () => {
    if (this.sdContext.designSituationProvider.state.dashboardState === DashboardState.configManager) return;

    this.sdContext.designSituationProvider.setDashboardState(DashboardState.configManager);
    this.sdContext.configManagerProvider.cancelCreatingOrEditingConfiguration();

    this.sdContext.notificationTemplateProvider.setCreateNotificationTemplateActive();
    this.sdContext.notificationTemplateProvider.clearNotificationTemplatePanel();
    this.sdContext.scheduledJobsProvider.cancelEditingScheduledJob();
    this.sdContext.apiManagementProvider.cancelEditingApi();
    this.sdContext.apiManagementProvider.cancelActiveMethod();
    this.sdContext.datasourceProvider.cancelCreatingOrEditingDatasource();
    this.sdContext.workflowProvider.clearSelectedWf();
  };

  redirectToHelpDesk = () => {
    if (this.sdContext.designSituationProvider.state.dashboardState === DashboardState.zendesk) return;

    this.sdContext.designSituationProvider.setDashboardState(DashboardState.zendesk);

    this.sdContext.notificationTemplateProvider.setCreateNotificationTemplateActive();
    this.sdContext.notificationTemplateProvider.clearNotificationTemplatePanel();
    this.sdContext.scheduledJobsProvider.cancelEditingScheduledJob();
    this.sdContext.apiManagementProvider.cancelEditingApi();
    this.sdContext.apiManagementProvider.cancelActiveMethod();
    this.sdContext.datasourceProvider.cancelCreatingOrEditingDatasource();
    this.sdContext.configManagerProvider.cancelCreatingOrEditingConfiguration();
    this.sdContext.workflowProvider.clearSelectedWf();
  };

  redirectToRunSettings = () => {
    if (this.sdContext.designSituationProvider.state.dashboardState === DashboardState.runSettings) return;

    this.sdContext.designSituationProvider.setDashboardState(DashboardState.runSettings);

    this.sdContext.notificationTemplateProvider.setCreateNotificationTemplateActive();
    this.sdContext.notificationTemplateProvider.clearNotificationTemplatePanel();
    this.sdContext.scheduledJobsProvider.cancelEditingScheduledJob();
    this.sdContext.apiManagementProvider.cancelEditingApi();
    this.sdContext.apiManagementProvider.cancelActiveMethod();
    this.sdContext.datasourceProvider.cancelCreatingOrEditingDatasource();
    this.sdContext.configManagerProvider.cancelCreatingOrEditingConfiguration();
    this.sdContext.workflowProvider.clearSelectedWf();
  };

  mergeStaticTableColumnsMessage = (message: IMergeStaticTableColumnsMessage) => {
    const sd = this.sdContext.screenProvider.getScreenDesign();
    const dict = this.sdContext.screenProvider.getScreenDesignDictionary();

    if (message.columnIds.length < 2) {
      KMainFunctions.displayWarningNotification("Please select at least two columns to merge.");
      return;
    }

    const firstColumnId = message.columnIds[0];
    const context = StaticTableHelper.getContext(sd, dict, firstColumnId.toString());
    if (!context) {
      KMainFunctions.displayWarningNotification("Could not find static table context");
      return;
    }

    const direction = StaticTableHelper.determineMergeDirection(context.bodyRows, message.columnPositions);

    if (direction === "invalid") {
      KMainFunctions.displayWarningNotification(
        "Selected columns must be in the same row for horizontal merge or in the same column position for vertical merge."
      );
      return;
    }

    let focusId: number | undefined;

    if (direction === "vertical") {
      focusId = StaticTableHelper.handleMergeColumnsVertically(sd, dict, message.columnPositions);
      if (focusId) {
        StaticTableHelper.recalculateAllMergedCellDimensions(context.tableDi, dict);
        this.sdContext.screenProvider.setScreenDesign(sd, ESdChangeReason.DragDropDi, focusId.toString());
      }
      KMainFunctions.displaySuccessNotification("Columns merged vertically successfully");
    } else {
      focusId = StaticTableHelper.handleMergeColumns(sd, dict, context, message.columnIds);
      if (focusId) {
        StaticTableHelper.recalculateAllMergedCellDimensions(context.tableDi, dict);
        this.sdContext.screenProvider.setScreenDesign(sd, ESdChangeReason.DragDropDi, focusId.toString());
      }
      KMainFunctions.displaySuccessNotification("Columns merged horizontally successfully");
    }
  };

  saveBlockAsCustomBlockMessage = (message: ISaveBlockAsCustomBlockMessage) => {
    this.sdContext.customBlocksProvider.handleOpenSaveBlockAsCustomBlockModal(message.selectedKuikaId);
  };

  redirectToBranching = () => {
    if (this.sdContext.designSituationProvider.state.dashboardState === DashboardState.branching) return;

    this.sdContext.designSituationProvider.setDashboardState(DashboardState.branching);

    this.sdContext.notificationTemplateProvider.setCreateNotificationTemplateActive();
    this.sdContext.notificationTemplateProvider.clearNotificationTemplatePanel();
    this.sdContext.scheduledJobsProvider.cancelEditingScheduledJob();
    this.sdContext.apiManagementProvider.cancelEditingApi();
    this.sdContext.apiManagementProvider.cancelActiveMethod();
    this.sdContext.datasourceProvider.cancelCreatingOrEditingDatasource();
    this.sdContext.configManagerProvider.cancelCreatingOrEditingConfiguration();
    this.sdContext.workflowProvider.clearSelectedWf();
  };
}
